const BASE = "";

/* ---- Offline queue (phone app) ----
 * A change made without a connection is kept on the device and sent when the connection returns.
 * Every change carries an Idempotency-Key, so a change that did reach the server before the
 * connection dropped is never applied twice. */

interface Queued { path: string; method: string; body?: string; key: string; at: string }
const QKEY = "saga-offline-queue";
const listeners = new Set<(n: number) => void>();
const readQ = (): Queued[] => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q: Queued[]) => {
  try { localStorage.setItem(QKEY, JSON.stringify(q)); } catch { /* storage full or blocked */ }
  listeners.forEach(fn => fn(q.length));
};
export const queueSize = () => readQ().length;
export function onQueueChange(fn: (n: number) => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }

export class OfflineError extends Error {}
const newKey = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2)}`);

let flushing = false;
/** Send what was queued offline, oldest first. Stops at the first network failure. */
export async function flushQueue(): Promise<void> {
  if (flushing || !readQ().length) return;
  flushing = true;
  try {
    for (;;) {
      const q = readQ();
      const item = q[0];
      if (!item) break;
      let res: Response;
      try {
        res = await fetch(item.path, { method: item.method, body: item.body, credentials: "include",
          headers: { "Content-Type": "application/json", "Idempotency-Key": item.key } });
      } catch { break; }   // still offline
      if (res.status === 409 && (await res.clone().json().catch(() => ({}))).code === "in_progress") break;
      writeQ(readQ().filter(x => x.key !== item.key));   // done — or refused for good (shown in the change's own screen)
    }
  } finally { flushing = false; }
}
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flushQueue(); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") flushQueue(); });
  setTimeout(() => flushQueue(), 2000);
}

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const method = (opts?.method || "GET").toUpperCase();
  const write = method !== "GET";
  const key = write ? newKey() : null;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: "include",
      ...opts,
      headers: { "Content-Type": "application/json", ...(key ? { "Idempotency-Key": key } : {}), ...opts?.headers },
    });
  } catch (err) {
    // No connection: keep the change and send it later (not for sign-in or AI calls, which need the server now).
    if (write && path.startsWith("/api/") && !/\/(ai|prepare|extract|suggest|send|test|preview)(\/|$|\?)/.test(path)) {
      writeQ([...readQ(), { path, method, body: typeof opts?.body === "string" ? opts.body : undefined, key: key!, at: new Date().toISOString() }]);
      throw new OfflineError("You're offline — the change is saved on this device and will be sent when you're back online");
    }
    throw err instanceof Error ? new Error(navigator.onLine ? "Couldn't reach the CRM — try again" : "You're offline") : err;
  }
  if (res.status === 401) {
    // Session expired mid-use: reload to the sign-in screen. Never redirect
    // for /auth/* probes (useAuth handles those), and never straight into
    // /auth/google — if the session cookie can't be set, that loops through
    // Google consent forever.
    if (!path.startsWith("/auth")) {
      window.location.href = "/";
    }
    throw new Error("Not authenticated");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
